import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AiOutlineUser, AiOutlineLock, AiOutlineCalendar, AiOutlineIdcard } from 'react-icons/ai';
import ROUTES, { SWITCH_ROUTES } from '@constants/routes';
import { DEVICE_TYPES, FEATURES } from '@constants/config';
import { 
    getDVCInLS, 
    setUkeyInLS,
    setJkeyInLS,
    setNkeyInLS,
    setUserRoleInLS,
    setDVCInLS
} from '@utils/ls';
import { getUserInfo, login, getAvatarImageUrl, setUserData } from '@api/user';
import { STATUS_CODE } from '@constants/api';
import { useGlobalProps } from '@hooks/useGlobalProps';
import { parseGetUserInfoRes, parseLoginRes } from '@utils/parseErrors';

export default function LoginPage() {
    const navigate = useNavigate();
    const { freePlayEnabled, setUser, setAvatarUrl, mDeivceId } = useGlobalProps();
    const [account, setAccount] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [birthday, setBirthday] = useState('');
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deviceType, setDeviceType] = useState(DEVICE_TYPES.BROWSER);
    const [needBirth, setNeedBirth] = useState(false);
    const [needFullName, setNeedFullName] = useState(false);

    useEffect(() => {
        // Detect device type
        const isNativeApp = window.navigator.standalone || 
                          window.matchMedia('(display-mode: standalone)').matches ||
                          document.referrer.includes('android-app://') ||
                          /android.*app/i.test(window.navigator.userAgent);
        
        setDeviceType(isNativeApp ? DEVICE_TYPES.NATIVE : DEVICE_TYPES.BROWSER);
    }, []);

    const handleSuccessfulLogin = async (userInfoRes, dvc) => {
        const { rtn, msg, ...userData } = userInfoRes;
        
        // Store device token for auto-login
        setDVCInLS(dvc);
        
        // Save user session data to localStorage
        setUkeyInLS(userInfoRes.ukey);
        setJkeyInLS(userInfoRes.jkey);
        setNkeyInLS(userInfoRes.nkey);
        setUserRoleInLS(userInfoRes.user_role);
        setUser(userData);

        // Get avatar
        try {
            const avatarRes = await getAvatarImageUrl(userData.acc);
            if (avatarRes.imageUrl) {
                setAvatarUrl(avatarRes.imageUrl);
            }
        } catch (e) {
            console.log('Avatar not found');
        }

        console.log('Login successful, navigating to main app');
        navigate(SWITCH_ROUTES.ROOT);
    };

    const handleLogin = async () => {
        setError('');
        setErrorCode(null);
        setLoading(true);

        try {
            // Use birthday as-is (should be MMDD format)
            const birthdayFormatted = birthday || '';
            
            const dvc = getDVCInLS() || mDeivceId || `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
            const dvctp = deviceType;
            
            // Call login API with the same structure as LoginModal
            const res = await login({
                acc: account,
                pwd: password,
                dvc,
                dvctp,
                bd: birthdayFormatted,
                unm: fullName || ''
            });

            let errorMsg;
            if (res) {
                const { rtn, dvctp, ukey } = res;
                console.log('Login API Response:', { rtn, dvctp, ukey, fullResponse: res });
                if (rtn === STATUS_CODE.SUCCESS) {
                    // Check if additional info is needed even on success
                    if (dvctp === 'b') {
                        // Need birthday - use setUserData API instead of login again
                        if (birthday) {
                            try {
                                const setUserDataRes = await setUserData({
                                    ukey: ukey,
                                    bd: birthdayFormatted
                                });
                                console.log('setUserData Response:', setUserDataRes);
                                if (setUserDataRes?.rtn === STATUS_CODE.SUCCESS) {
                                    // Birthday updated successfully, proceed to get user info
                                    const userInfoRes = await getUserInfo({ dvc, ukey });
                                    if (userInfoRes?.rtn === STATUS_CODE.SUCCESS) {
                                        handleSuccessfulLogin(userInfoRes, dvc);
                                        return;
                                    }
                                } else {
                                    errorMsg = 'Failed to update birthday information';
                                }
                            } catch (e) {
                                errorMsg = 'Error updating birthday information';
                            }
                        } else {
                            // Need birthday input
                            setNeedBirth(true);
                            errorMsg = 'Please enter your birthday (DDMM format)';
                            setError(errorMsg);
                            setErrorCode('dvctp_b');
                        }
                    } else if (dvctp === 'n') {
                        // Need full name - use setUserData API instead of login again  
                        if (fullName && fullName.length >= 5) {
                            try {
                                const setUserDataRes = await setUserData({
                                    ukey: ukey,
                                    unm: fullName
                                });
                                console.log('setUserData Response:', setUserDataRes);
                                if (setUserDataRes?.rtn === STATUS_CODE.SUCCESS) {
                                    // Full name updated successfully, proceed to get user info
                                    const userInfoRes = await getUserInfo({ dvc, ukey });
                                    if (userInfoRes?.rtn === STATUS_CODE.SUCCESS) {
                                        handleSuccessfulLogin(userInfoRes, dvc);
                                        return;
                                    }
                                } else {
                                    errorMsg = 'Failed to update full name information';
                                }
                            } catch (e) {
                                errorMsg = 'Error updating full name information';
                            }
                        } else {
                            // Need full name input or name too short
                            setNeedFullName(true);
                            errorMsg = fullName ? 'Full name must be at least 5 characters long' : 'Please enter your full name';
                            setError(errorMsg);
                            setErrorCode('dvctp_n');
                        }
                    } else {
                        // Login successful, get user info
                        const userInfoRes = await getUserInfo({ dvc });
                        if (userInfoRes?.rtn === STATUS_CODE.SUCCESS) {
                            handleSuccessfulLogin(userInfoRes, dvc);
                            return;
                        } else {
                            errorMsg = parseGetUserInfoRes({}, userInfoRes);
                        }
                    }

                } else if (rtn === STATUS_CODE.NEED_BIRTH) {
                    // Birthday missing error (code 95)
                    setNeedBirth(true);
                    errorMsg = 'Please enter your birthday (DDMM format)';
                    setError(errorMsg);
                    setErrorCode(rtn);
                } else if (rtn === STATUS_CODE.NEED_FULLNAME) {
                    // Full name missing error (code 98)
                    setNeedFullName(true);
                    errorMsg = 'Please enter your full name (minimum 5 characters)';
                    setError(errorMsg);
                    setErrorCode(rtn);
                } else {
                    errorMsg = parseLoginRes({}, res);
                }
            } else {
                errorMsg = 'Login failed. Please check your connection.';
            }

            if (errorMsg) {
                setError(errorMsg);
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Network error. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    // Store device token on successful login
    const storeDeviceToken = (dvc) => {
        if (dvc) {
            setDVCInLS(dvc);
            console.log('Device token stored for auto-login');
        }
    };

    return (
        <Container>
            <LogoWrapper>
                <Logo src="/images/acewin_logo_2.png" alt="Logo" />
            </LogoWrapper>
            <LoginFormContainer>
                <LoginTitle>Login</LoginTitle>
                
                <Form>
                    <InputGroup>
                        <InputIcon>
                            <AiOutlineUser />
                        </InputIcon>
                        <Input
                            type="text"
                            value={account}
                            onChange={(e) => setAccount(e.target.value)}
                            placeholder="Username/ID"
                            disabled={loading}
                        />
                    </InputGroup>

                    <InputGroup>
                        <InputIcon>
                            <AiOutlineLock />
                        </InputIcon>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            disabled={loading}
                        />
                    </InputGroup>

                    {needFullName && (
                        <InputGroup>
                            <InputIcon>
                                <AiOutlineIdcard />
                            </InputIcon>
                            <Input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="Full Name (min 5 characters)"
                                minLength={5}
                                disabled={loading}
                            />
                        </InputGroup>
                    )}

                    {needBirth && (
                        <InputGroup>
                            <InputIcon>
                                <AiOutlineCalendar />
                            </InputIcon>
                            <Input
                                type="text"
                                value={birthday}
                                onChange={(e) => setBirthday(e.target.value)}
                                placeholder="Birthday (DDMM)"
                                maxLength={4}
                                disabled={loading}
                            />
                        </InputGroup>
                    )}

                    {error && (
                        <ErrorMessage>
                            {error}
                            {errorCode && <ErrorCode>Error Code: {errorCode}</ErrorCode>}
                        </ErrorMessage>
                    )}

                    <LoginButton onClick={handleLogin} disabled={loading || !account || !password}>
                        {loading ? 'Logging in...' : 'Login'}
                    </LoginButton>
                </Form>
            </LoginFormContainer>
        </Container>
    );
}

const Container = styled.div`
    min-height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: url('/images/new_background.jpg') center center / cover no-repeat,
                radial-gradient(ellipse at center, #1e3c72 0%, #2a5298 50%, #0f1419 100%);
    padding: 20px;
    position: relative;

    @media (min-width: 768px) {
        justify-content: center;
        padding: 40px 20px;
    }
`;

const LogoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 20px;
    position: relative;
    z-index: 10;
`;

const Logo = styled.img`
    max-width: 450px;
    max-height: 300px;
    object-fit: contain;
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
    margin-bottom: 5px;
    
    @media (max-width: 768px) {
        max-width: 250px;
        max-height: 150px;
    }
    
    @media (max-width: 480px) {
        max-width: 200px;
        max-height: 120px;
    }
`;

const Tagline = styled.div`
    color: #FFA500;
    font-size: 1.2rem;
    font-weight: 500;
    letter-spacing: 4px;
`;

const LoginFormContainer = styled.div`
    background: rgba(20, 50, 75, 0.25);
    backdrop-filter: blur(10px);
    border-radius: 15px;
    border: 1px solid rgba(0, 189, 255, 0.3);
    padding: 30px;
    width: 100%;
    max-width: 380px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5), 
                inset 0 1px rgba(255, 255, 255, 0.1),
                0 0 20px rgba(0, 189, 255, 0.3);
    position: relative;
    z-index: 10;
`;

const LoginTitle = styled.h2`
    color: #ffffff;
    text-align: center;
    font-size: 1.5rem;
    margin-bottom: 25px;
    font-weight: 500;
`;

const Form = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const InputGroup = styled.div`
    position: relative;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 25px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
    height: 50px;
    
    &:focus-within {
        border-color: rgba(255, 255, 255, 0.3);
        box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
        background: rgba(255, 255, 255, 0.08);
    }
`;

const InputIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 15px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 1.2rem;
`;

const Input = styled.input`
    width: 100%;
    background: transparent;
    border: none;
    padding: 0 15px 0 0;
    color: #ffffff;
    font-size: 1rem;
    height: 100%;
    
    &:focus {
        outline: none;
    }
    
    &::placeholder {
        color: rgba(255, 255, 255, 0.4);
        font-weight: 300;
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ErrorMessage = styled.div`
    background: rgba(255, 59, 48, 0.2);
    border: 1px solid rgba(255, 59, 48, 0.5);
    border-radius: 8px;
    padding: 12px;
    color: #ff3b30;
    font-size: 0.85rem;
    text-align: center;
`;


const LoginButton = styled.button`
    background: rgba(255, 255, 255, 0.9);
    color: #1a1a1a;
    border: none;
    padding: 15px 30px;
    border-radius: 25px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 20px;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    
    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 1);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    }
    
    &:disabled {
        background: rgba(255, 255, 255, 0.5);
        color: rgba(0, 0, 0, 0.5);
        cursor: not-allowed;
        transform: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    
    &:active:not(:disabled) {
        transform: translateY(0);
    }
`;

const ErrorCode = styled.div`
    margin-top: 8px;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
`;

